import Link from 'next/link';

import { linkResolver } from '@/sanity/lib/utils';

interface ResolvedLinkProps {
  link: any;
  children: React.ReactNode;
  className?: string;
}

export function ResolvedLink({ link, children, className }: ResolvedLinkProps) {
  // resolveLink() is used to determine the type of link and return the appropriate URL.
  const resolvedLink = linkResolver(link);

  if (typeof resolvedLink === 'string' && resolvedLink !== null) {
    // Check if link should open in new tab
    const shouldOpenInNewTab =
      link?.type === 'external' ||
      link?.openInNewTab ||
      link?.newTab ||
      link?.isOpenInNewTab ||
      resolvedLink.startsWith('http') ||
      resolvedLink.startsWith('https') ||
      resolvedLink.startsWith('//');

    // Determine if it's an external link (always use <a> tag for external)
    const isExternal =
      link?.type === 'external' ||
      resolvedLink.startsWith('http') ||
      resolvedLink.startsWith('https') ||
      resolvedLink.startsWith('//') ||
      resolvedLink.startsWith('mailto:') ||
      resolvedLink.startsWith('tel:');

    // For external links or links that should open in new tab, use <a> tag
    if (isExternal || shouldOpenInNewTab) {
      return (
        <a
          href={resolvedLink}
          target={shouldOpenInNewTab || isExternal ? '_blank' : undefined}
          rel={shouldOpenInNewTab || isExternal ? 'noopener noreferrer' : undefined}
          className={className}
        >
          {children}
        </a>
      );
    }

    // For internal links, use Next.js Link component
    return (
      <Link href={resolvedLink} className={className}>
        {children}
      </Link>
    );
  }

  // Fallback: render as plain text if link resolution fails
  return <span className={className}>{children}</span>;
}
